// Swift's `Character` and `CharacterSet` predicates, over code points. Strings here are indexed by
// code point, as Swift indexes by character; the texts this reads have no multi-scalar graphemes
// that would tell the two apart.

const letter = /^\p{Alphabetic}$/u;
const number = /^\p{N}$/u;
const whitespace = /^\p{White_Space}$/u;

export function chars(text: string): string[] {
  return Array.from(text);
}

export function isLetter(c: string | undefined): boolean {
  return c !== undefined && letter.test(firstScalar(c));
}

export function isNumber(c: string | undefined): boolean {
  return c !== undefined && number.test(firstScalar(c));
}

export function isWhitespace(c: string | undefined): boolean {
  return c !== undefined && whitespace.test(firstScalar(c));
}

export function isUppercase(c: string | undefined): boolean {
  return c !== undefined && c !== c.toLowerCase();
}

export function isLowercase(c: string | undefined): boolean {
  return c !== undefined && c !== c.toUpperCase();
}

export function isASCII(c: string | undefined): boolean {
  return c !== undefined && c.length === 1 && c.charCodeAt(0) < 128;
}

function firstScalar(c: string): string {
  const code = c.codePointAt(0);
  return code === undefined ? c : String.fromCodePoint(code);
}

export function hasLetter(text: string): boolean {
  return chars(text).some(isLetter);
}

export function count(text: string): number {
  let n = 0;
  for (const _ of text) n += 1;
  return n;
}

/** `Int(String)`: plain ASCII digits, or nothing. */
export function toInt(text: string): number | null {
  if (!/^[0-9]+$/.test(text)) return null;
  const value = Number(text);
  return Number.isSafeInteger(value) ? value : null;
}

const newlineChars = ["\n", "\u000B", "\u000C", "\r", "\u0085", "\u2028", "\u2029"];

function isInWhitespaces(c: string): boolean {
  return c === "\t" || /^\p{Zs}$/u.test(c);
}

function isInWhitespacesAndNewlines(c: string): boolean {
  return isInWhitespaces(c) || newlineChars.includes(c);
}

function trimBy(text: string, test: (c: string) => boolean): string {
  const list = chars(text);
  let start = 0;
  let end = list.length;
  while (start < end && test(list[start]!)) start += 1;
  while (end > start && test(list[end - 1]!)) end -= 1;
  return list.slice(start, end).join("");
}

/** `trimmingCharacters(in: .whitespaces)`. */
export function trimWS(text: string): string {
  return trimBy(text, isInWhitespaces);
}

/** `trimmingCharacters(in: .whitespacesAndNewlines)`. */
export function trimWSNL(text: string): string {
  return trimBy(text, isInWhitespacesAndNewlines);
}

export function trimSet(text: string, set: string): string {
  const members = new Set(chars(set));
  return trimBy(text, (c) => members.has(c));
}

/** `components(separatedBy: .newlines)`: every newline character splits, so `\r\n` leaves an empty line. */
export function splitNewlines(text: string): string[] {
  return text.split(/[\n\u000B\u000C\r\u0085\u2028\u2029]/u);
}

/** `split(separator:)` with empty pieces dropped. */
export function splitOmitting(text: string, test: (c: string) => boolean): string[] {
  const out: string[] = [];
  let current = "";
  for (const c of text) {
    if (test(c)) {
      if (current !== "") out.push(current);
      current = "";
    } else {
      current += c;
    }
  }
  if (current !== "") out.push(current);
  return out;
}

export function splitSpaces(text: string): string[] {
  return splitOmitting(text, (c) => c === " ");
}

/** `folding(options: [.diacriticInsensitive, .widthInsensitive])`. */
export function fold(text: string): string {
  const narrowed = Array.from(text, (c) => {
    const code = c.codePointAt(0)!;
    if (code >= 0xff01 && code <= 0xff5e) return String.fromCodePoint(code - 0xfee0);
    if (code === 0x3000) return " ";
    return c;
  }).join("");
  return narrowed.normalize("NFD").replace(/\p{Mn}/gu, "").normalize("NFC");
}

export function dropLast(text: string, n = 1): string {
  const list = chars(text);
  return list.slice(0, Math.max(0, list.length - n)).join("");
}




export function first(text: string): string | undefined {
  for (const c of text) return c;
  return undefined;
}


/** ICU's `\b`, which counts letters of any script as word characters. */
const word = String.raw`[\p{Alphabetic}\p{M}\p{Nd}\p{Pc}‌‍]`;
export const B = String.raw`(?:(?<=${word})(?!${word})|(?<!${word})(?=${word}))`;
export const S = String.raw`\p{White_Space}`;
export const D = String.raw`\p{Nd}`;
